import CommonTitleBar from "components/common/CommonTitleBar";
import DeleteConfirmationModal from "components/common/ConfirmationDialog";
import CustomField from "components/common/CustomField";
import CustomLabel from "components/common/CustomLabel";
import FileUpload from "components/common/FileUpload";
import Filters from "components/common/Filters";
import IsLoading from "components/common/IsLoading";
import NoDataFound from "components/common/NoDataFound";
import Paginations from "components/common/Paginations";
import Spinners from "components/common/Spinners";
import {
  formatFilterOptions,
  getKeywordValue,
  getMinMaxDate,
  handleStopPropagation,
} from "components/common/Utils";
import { addAmenitySchema } from "components/common/Validation";
import { orderByOptions, statusOptions } from "components/constants/StatusKeys";
import TableShimmer from "components/ShimmerEffects/CommonShimmer/TableShimmer";
import { Form, Formik } from "formik";
import { Ellipsis, X } from "lucide-react";
import { InputSwitch } from "primereact/inputswitch";
import React, { useEffect, useState } from "react";
import {
  Badge,
  Col,
  Container,
  Dropdown,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  addAmenity,
  deleteAmenity,
  getAllAmenities,
  getAmenityDetails,
  updateAmenity,
  updateAmenityStatus,
} from "store/actions/amenitySlice";
import { getOrganistionList } from "store/actions/organizationSlice";
import { showAlert } from "store/reducers/alertReducer";

const Amenities = () => {
  const { t } = useTranslation();
  document.title = t("Amenities | Work Space");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { search } = useLocation();
  const pageNumber = Number(new URLSearchParams(search).get("page")) || 1;
  const { organisationsList } = useSelector((state) => state.Organizations);

  const {
    isLoading,
    loading,
    perPage,
    totalCount,
    totalPages,
    currentPage,
    amenities,
    amenityDetails,
    gettingDetails,
  } = useSelector((state) => state.Amenities);

  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [show, setShow] = useState(false); // ADD/EDIT MODAL
  const [showModal, setShowModal] = useState(false); // DELETE MODAL
  const [keyword, setKeyword] = useState(null);
  const [dates, setDates] = useState([]);

  const [selectedFilters, setSelectedFilters] = useState({
    status: -1,
    orderBy: -1,
    organizationId: "",
  });

  const [formValues, setFormValues] = useState({
    name: null,
    nameAr: null,
    image: null,
  });

  const handleClose = () => {
    setShow(false);
    setModalType(null);
    setFormValues({
      name: null,
      nameAr: null,
      image: [],
    });
  };
  const handleShow = () => setShow(true);

  useEffect(() => {
    getAllData();
    dispatch(getOrganistionList());
  }, []);

  const getAllData = (page, action) => {
    const { actionType } = action || {}; // SEARCH, RESET, RESET_KEYWORD
    const { status, orderBy , organizationId } = selectedFilters;
    const isReset = actionType === "RESET";

    const payload = {
      perPage,
      pageNo: page || pageNumber,
      status: isReset ? -1 : status,
      orderBy: isReset ? -1 : orderBy,
      dateRange: isReset ? {} : getMinMaxDate(dates),
      keyword: getKeywordValue(actionType, keyword),
      organizationId: isReset ? "" : organizationId,
    };
    dispatch(getAllAmenities(payload));
    navigate(`/management/amenities?page=${page || pageNumber}`);
  };

  const handleStatusChange = (id, status) => {
    setSelectedId(id);
    const payload = {
      id,
      status: status === 1 ? 0 : 1,
    };

    dispatch(updateAmenityStatus(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          setSelectedId(null);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
          setSelectedId(null);
        }
      })
      .catch((err) => {});
  };

  useEffect(() => {
    const getDetails = () => {
      const payload = {
        id: selectedId,
      };
      dispatch(getAmenityDetails(payload));
    };
    if (modalType === "EDIT") {
      getDetails();
    }
  }, [modalType]);

  // SET FORM FIELD
  useEffect(() => {
    if (amenityDetails && !gettingDetails) {
      const { name, nameAr, imageUrl } = amenityDetails || {};
      setFormValues({
        name,
        nameAr,
        image: imageUrl ? [imageUrl] : [],
      });
    }
  }, [amenityDetails, gettingDetails]);

  const handleSubmit = (values, { resetForm }) => {
    const { name, nameAr, image } = values;

    let data = new FormData();
    data.append("name", name);
    data.append("nameAr", nameAr);
    data.append("imageUrl", image[0] || "");

    if (modalType === "ADD") {
      dispatch(addAmenity(data))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            handleClose();
            resetForm();
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
            handleClose();
          }
        })
        .catch((err) => {});
    } else if (modalType === "EDIT") {
      data.append("id", selectedId);
      dispatch(updateAmenity(data))
        .then((action) => {
          const { statusCode, success, message } = action?.payload || {};
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            handleClose();
            resetForm();
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
            handleClose();
          }
        })
        .catch((err) => {});
    }
  };

  const handleDelete = () => {
    const payload = {
      id: selectedId,
      status: "2", // 2 FOR DELETE
    };
    dispatch(deleteAmenity(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          setShowModal(false);
          setSelectedId(null);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  const handleReset = () => {
    setKeyword("");
    setDates([]);
    setSelectedFilters({ orderBy: -1, status: -1 });
    getAllData(pageNumber, { actionType: "RESET" });
  };

  const filters = [
    {
      key: "status",
      placeholder: "Status",
      options: statusOptions,
      type: "singleSelect",
    },
    {
      key: "orderBy",
      placeholder: "Order By",
      options: orderByOptions,
      type: "singleSelect",
    },
    {
      key: "organizationId",
      placeholder: "Organization",
      options: formatFilterOptions(organisationsList),
      type: "singleSelect",
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content common_page">
        <Container fluid>
          <CommonTitleBar
            title={t("Amenities")}
            isCreateBtn={true}
            createBtnText={t("Add New")}
            onCreateBtnClick={() => {
              setModalType("ADD");
              handleShow();
            }}
          />
          <div className="common_wrapper">
            <Filters
              keyword={keyword}
              setKeyword={setKeyword}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              dates={dates}
              setDates={setDates}
              filters={filters}
              onSearch={getAllData}
              onReset={handleReset}
            />
            <Row>
              <Col lg={12}>
                {loading ? (
                  <TableShimmer col={6} />
                ) : amenities.length === 0 ? (
                  <NoDataFound text={t("No amenities found")}/>
                ) : (
                  <div
                    className="common_table_style"
                    style={{ minHeight: showDropdown ? 210 : "unset" }}
                  >
                    <Table>
                      <thead className="t_head">
                        <tr>
                          <th>#</th>
                          <th>{t("Icon")}</th>
                          <th>{t("Name(English)")}</th>
                          <th>{t("Name(Arabic)")}</th>
                          <th>{t("Status")}</th>
                          <th>{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {amenities?.map((item, index) => (
                          <tr key={item?._id}>
                            <td>{(currentPage - 1) * perPage + index + 1}</td>
                            <td>
                              {item.imageUrl ? (
                                <img
                                  width={30}
                                  height={30}
                                  src={item.imageUrl}
                                  alt=""
                                />
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>{item.name}</td>
                            <td>{item.nameAr}</td>
                            <td>
                              <Badge
                                bg={
                                  item.status === 1
                                    ? " bg_primary"
                                    : " bg-secondary"
                                }
                              >
                                {item.status === 1
                                  ? t("Active")
                                  : t("Deactive")}
                              </Badge>
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-3">
                                <InputSwitch
                                  checked={item.status === 1}
                                  disabled={
                                    isLoading && selectedId === item._id
                                  }
                                  onChange={(e) =>
                                    handleStatusChange(item._id, item.status)
                                  }
                                />
                                <Dropdown
                                  className="custom_dropdown list_action_dropdown"
                                  drop={"down"}
                                  onClick={handleStopPropagation}
                                  onToggle={(isOpen) => setShowDropdown(isOpen)}
                                >
                                  <Dropdown.Toggle>
                                    <Ellipsis className="text_black" />
                                  </Dropdown.Toggle>

                                  <Dropdown.Menu align="end">
                                    <Dropdown.Item
                                      href="#"
                                      onClick={() => {
                                        setSelectedId(item._id);
                                        setModalType("EDIT");
                                        handleShow();
                                      }}
                                    >
                                      {t("Edit")}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      href="#"
                                      className="text_danger"
                                      onClick={() => {
                                        setSelectedId(item._id);
                                        setShowModal(true);
                                      }}
                                    >
                                      {t("Delete")}
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Col>
            </Row>
          </div>
          {!loading && (
            <Paginations
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                getAllData(page);
              }}
              totalCount={totalCount}
            />
          )}
        </Container>
      </div>
      <Modal centered show={show} onHide={handleClose} className="common_modal">
        <div className="modal_header g_between">
          <h2 className="fs_17">
            {modalType === "ADD" ? t("Add Amenity") : t("Edit Amenity")}
          </h2>
          <X
            onClick={handleClose}
            className="text_black c_p"
            width={20}
            height={20}
          />
        </div>
        <div className="modal_body mt-0">
          {modalType === "EDIT" && gettingDetails ? (
            <IsLoading />
          ) : (
            <div>
              <Formik
                initialValues={formValues}
                validationSchema={addAmenitySchema}
                enableReinitialize={true}
                onSubmit={handleSubmit}
              >
                {({ errors, touched, values, setFieldValue }) => (
                  <Form>
                    <Col lg={12}>
                      <div className="form_field mt_13 mb-0">
                        <CustomLabel htmlFor="name" label={t("English Name")} />
                        <CustomField
                          name="name"
                          type="text"
                          placeholder={t("Enter English Name")}
                          touched={touched}
                          errors={errors}
                        />
                      </div>
                    </Col>
                    <Col lg={12}>
                      <div className="form_field mt_13 mb-0">
                        <CustomLabel htmlFor="name" label={t("Arabic Name")} />
                        <CustomField
                          name="nameAr"
                          type="text"
                          placeholder={t("Enter Arabic Name")}
                          touched={touched}
                          errors={errors}
                          isArabic={true}
                        />
                      </div>
                    </Col>
                    <Col lg={12} className="mt-4">
                      <FileUpload
                        uploadedFiles={values.image || []}
                        setUploadedFiles={(files) =>
                          setFieldValue("image", files)
                        }
                        maxFiles={1}
                        maxFileSize={0.1 * 1024 * 1024} // 5 MB
                        acceptedFileTypes={[
                          "image/png",
                          "image/jpeg",
                          "image/svg+xml",
                        ]}
                        requireSquare={true}
                        requireRectangle={false}
                        className={
                          touched?.image && errors.image ? "invalid" : ""
                        }
                      />
                    </Col>
                    <Col lg={12}>
                      <div className="g_between mt-4 gap-3">
                        <button
                          type="button"
                          className="cancel_btn w-50"
                          onClick={handleClose}
                        >
                          {t("Cancel")}
                        </button>
                        <button
                          type="submit"
                          className="main_btn w-50"
                          disabled={isLoading}
                        >
                          {modalType === "ADD" ? t("Add") : t("Save")}
                        </button>
                      </div>
                    </Col>
                  </Form>
                )}
              </Formik>
            </div>
          )}
        </div>
      </Modal>
      <DeleteConfirmationModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedId(null);
        }}
        onConfirm={handleDelete}
        message={t("Are you sure you want to delete this {{name}}?", {
          name: "Amenity",
        })}
        isLoading={isLoading}
      />
    </React.Fragment>
  );
};

export default Amenities;
